import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { joinLeagueByInviteCode } from '../services/leagueService';
import { FIRST_KICKOFF_UTC, getNow } from '../utils/lockUtils';
import './JoinLeague.css';

/**
 * Handles /join/:inviteCode
 *
 * Logged in  → auto-join → navigate into the league
 * Logged out → store inviteCode in sessionStorage → redirect to / (Auth page)
 *              Auth.js picks it up after login/signup, calls joinLeagueByInviteCode,
 *              then navigates to /leagues
 * Expired    → show expired-link message (links deactivate at first kickoff)
 */
export default function JoinLeague() {
  const { inviteCode } = useParams();
  const { currentUser, loading } = useAuth();
  const navigate = useNavigate();
  const [expired, setExpired] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (loading) return;

    // Check expiry before anything else (no auth needed)
    if (getNow() >= FIRST_KICKOFF_UTC.getTime()) {
      setExpired(true);
      return;
    }

    if (!currentUser) {
      sessionStorage.setItem('pendingInviteCode', inviteCode);
      navigate('/login', { replace: true });
      return;
    }

    // Authenticated — join and navigate into the league
    joinLeagueByInviteCode(inviteCode, currentUser.uid)
      .then((leagueId) => navigate(`/league/${leagueId}`, { replace: true }))
      .catch((err) => {
        if (err.message === 'EXPIRED') {
          setExpired(true);
        } else {
          setInvalid(true);
        }
      });
  }, [loading, currentUser, inviteCode, navigate]);

  if (invalid) {
    return (
      <div className="join-expired-wrap">
        <div className="join-expired-card">
          <h2 className="join-expired-title">Invalid Invite Link</h2>
          <p className="join-expired-body">
            This invite link is not valid. It may have been reset by the commissioner. Ask them for an updated link.
          </p>
          <button className="join-expired-btn" onClick={() => navigate('/leagues')}>
            Go to My Leagues
          </button>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="join-expired-wrap">
        <div className="join-expired-card">
          <div className="join-expired-icon">
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h2 className="join-expired-title">Invite Link Expired</h2>
          <p className="join-expired-body">
            Invite links are deactivated once the tournament kicks off. Browse public leagues to join one, or ask the commissioner for a direct add.
          </p>
          <button className="join-expired-btn" onClick={() => navigate('/directory')}>
            Browse Public Leagues
          </button>
        </div>
      </div>
    );
  }

  return null;
}
